import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { apiClient, getApiErrorMessage } from "@/lib/apiClient";
import { toast } from "sonner";

export interface InfoNutricional {
  id: number;
  produtoId: number;
  subProdutoId: number;
  grupoId: number;
  unidadePorcaoId: number;
  medidaCaseiraId: number;
  parteDecimalId: number;
  qtdPorcaoEmbalagem: number;
  calculoAutomaticoQtdPorcao: boolean;
  imprimeLactoseGalactose: boolean;
  qtdPorcao: number;
  qtdPorcaoMedidaCaseira: number;
  qtdCalorias: number;
  qtdCarboidrato: number;
  qtdFibraAlimentar: number;
  qtdGorduraTotais: number;
  qtdGorduraSaturada: number;
  qtdGorduraTrans: number;
  qtdProteinas: number;
  qtdSodio: number;
  qtdCalcio: number;
  qtdFerro: number;
  qtdAcucaresTotais: number;
  qtdAcucaresAdicionados: number;
  qtdVitaminaA: number;
  qtdVitaminaC: number;
  qtdLactose: number;
  qtdGalactose: number;
  perCalorias: number;
  perCarboidrato: number;
  perFibraAlimentar: number;
  perGorduraTotais: number;
  perGorduraSaturada: number;
  perGorduraTrans: number;
  perProteinas: number;
  perSodio: number;
  perCalcio: number;
  perFerro: number;
  perAcucaresTotais: number;
  perAcucaresAdicionados: number;
}

export type InfoNutricionalRequest = Omit<InfoNutricional, "id" | "produtoId">;

export const INFO_NUTRICIONAL_DEFAULT: InfoNutricionalRequest = {
  subProdutoId: 0,
  grupoId: 0,
  unidadePorcaoId: 0,
  medidaCaseiraId: 0,
  parteDecimalId: 0,
  qtdPorcaoEmbalagem: 0,
  calculoAutomaticoQtdPorcao: false,
  imprimeLactoseGalactose: false,
  qtdPorcao: 0,
  qtdPorcaoMedidaCaseira: 0,
  qtdCalorias: 0,
  qtdCarboidrato: 0,
  qtdFibraAlimentar: 0,
  qtdGorduraTotais: 0,
  qtdGorduraSaturada: 0,
  qtdGorduraTrans: 0,
  qtdProteinas: 0,
  qtdSodio: 0,
  qtdCalcio: 0,
  qtdFerro: 0,
  qtdAcucaresTotais: 0,
  qtdAcucaresAdicionados: 0,
  qtdVitaminaA: 0,
  qtdVitaminaC: 0,
  qtdLactose: 0,
  qtdGalactose: 0,
  perCalorias: 0,
  perCarboidrato: 0,
  perFibraAlimentar: 0,
  perGorduraTotais: 0,
  perGorduraSaturada: 0,
  perGorduraTrans: 0,
  perProteinas: 0,
  perSodio: 0,
  perCalcio: 0,
  perFerro: 0,
  perAcucaresTotais: 0,
  perAcucaresAdicionados: 0,
};

export const useInfoNutricional = (produtoId: number | undefined) => {
  const queryClient = useQueryClient();

  const query = useQuery({
    queryKey: ["info-nutricional", produtoId],
    queryFn: async () => {
      const { data } = await apiClient.get<InfoNutricional>(
        `/api/produtos/${produtoId}/info-nutricional`
      );
      return data;
    },
    enabled: !!produtoId,
    retry: (failureCount, error: unknown) => {
      // Não tentar novamente em 404 (produto sem info nutricional cadastrada)
      if ((error as { response?: { status?: number } })?.response?.status === 404) return false;
      return failureCount < 2;
    },
  });

  const saveMutation = useMutation({
    mutationFn: async (body: InfoNutricionalRequest) => {
      const { data } = await apiClient.put<InfoNutricional>(
        `/api/produtos/${produtoId}/info-nutricional`,
        body
      );
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["info-nutricional", produtoId] });
      toast.success("Informação nutricional salva com sucesso!");
    },
    onError: (error) => {
      toast.error(`Erro ao salvar informação nutricional: ${getApiErrorMessage(error)}`);
    },
  });

  return {
    infoNutricional: query.data,
    isLoading:       query.isLoading,
    isNotFound:      (query.error as { response?: { status?: number } } | null)?.response?.status === 404,
    saveInfoNutricional: saveMutation.mutate,
    isSaving:        saveMutation.isPending,
  };
};
